import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { SiteLayout } from "@/components/SiteLayout";
import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { WhatsAppActions } from "@/components/WhatsAppActions";
import { POSTS } from "@/lib/blog-posts";
import {
  getPublishedArticleBySlug,
  listPublishedArticles,
} from "@/lib/cms/journal.functions";
import {
  ARTICLES_FALLBACK,
  JOURNAL_PLACEHOLDER,
  deriveAlt,
  formatArticleDate,
  imageForSlug,
  isValidHttpsUrl,
  type ArticleItem,
} from "@/lib/journal-defaults";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    // Server-side existence check so unknown slugs return a real 404.
    const result = await getPublishedArticleBySlug({ data: { slug: params.slug } });
    if (result.kind === "not_found") throw notFound();
    if (result.kind === "found") {
      const a = result.article;
      return {
        post: {
          title: a.title,
          excerpt: a.excerpt ?? "",
          image: isValidHttpsUrl(a.imageUrl) ? a.imageUrl : null,
          author: a.author ?? "",
          date: a.publishedAt ?? "",
          category: a.category ?? "",
        },
      };
    }
    // Read error: fall back to bundled content for head only.
    const post = POSTS.find((p) => p.slug === params.slug) ?? null;
    return { post };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Article not found — Poonam Kapur Healthy Kitchen" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { post } = loaderData;
    if (!post) {
      const url = `https://poonamkapur.com/blog/${params.slug}`;
      return {
        meta: [
          { title: "Article — Journal — Poonam Kapur Healthy Kitchen" },
          { name: "robots", content: "noindex" },
          {
            name: "description",
            content:
              "An article from the Poonam Kapur Healthy Kitchen Journal.",
          },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const url = `https://poonamkapur.com/blog/${params.slug}`;
    return {
      meta: [
        { title: `${post.title} — Journal — Poonam Kapur Healthy Kitchen` },
        { name: "description", content: post.excerpt },
        { property: "og:title", content: post.title },
        { property: "og:description", content: post.excerpt },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        ...(post.image ? [{ property: "og:image", content: post.image }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: post.title },
        { name: "twitter:description", content: post.excerpt },
        ...(post.image ? [{ name: "twitter:image", content: post.image }] : []),
        { name: "author", content: post.author },
        { property: "article:published_time", content: post.date },
        { property: "article:section", content: post.category },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.excerpt,
            author: { "@type": "Person", name: post.author },
            datePublished: post.date,
            image: post.image ?? undefined,
            mainEntityOfPage: url,
            publisher: {
              "@type": "Organization",
              name: "Poonam Kapur Healthy Kitchen",
              url: "https://poonamkapur.com",
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: PostNotFound,
  errorComponent: ({ reset }) => (
    <SiteLayout>
      <Section>
        <h1 className="font-display text-3xl">Something went wrong.</h1>
        <button onClick={reset} className="mt-4 text-sm text-primary hover:underline">
          Try again
        </button>
      </Section>
    </SiteLayout>
  ),
  component: PostPage,
});

function PostNotFound() {
  return (
    <SiteLayout>
      <Section>
        <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
        <span className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
          Not found
        </span>
        <h1 className="mt-4 font-display text-4xl md:text-5xl">
          We couldn't find that article.
        </h1>
        <p className="mt-6 max-w-xl text-muted-foreground">
          The piece you're looking for may have been moved or is not part of this
          demo preview.
        </p>
        <Link
          to="/blog"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden strokeWidth={1.75} />
          Back to Journal
        </Link>
      </Section>
    </SiteLayout>
  );
}

function PostPage() {
  const { slug } = Route.useParams();
  const fetchOne = useServerFn(getPublishedArticleBySlug);
  const fetchList = useServerFn(listPublishedArticles);

  const detailQuery = useQuery({
    queryKey: ["journal", "detail", slug],
    queryFn: () => fetchOne({ data: { slug } }),
    staleTime: 60_000,
  });
  const listQuery = useQuery({
    queryKey: ["journal", "public"],
    queryFn: () => fetchList(),
    staleTime: 60_000,
  });

  if (detailQuery.isLoading) {
    return (
      <SiteLayout>
        <Section>
          <p className="text-sm text-muted-foreground">Loading…</p>
        </Section>
      </SiteLayout>
    );
  }

  const result = detailQuery.data;
  let post: ArticleItem | null = null;

  if (result && result.kind === "found") {
    const r = result.article;
    const image = isValidHttpsUrl(r.imageUrl)
      ? r.imageUrl
      : imageForSlug(r.slug) ?? JOURNAL_PLACEHOLDER;
    post = {
      id: r.id,
      slug: r.slug,
      category: r.category,
      title: r.title,
      excerpt: r.excerpt,
      date: formatArticleDate(r.publishedAt),
      readTime: r.readTime,
      author: r.author,
      image,
      alt: deriveAlt(r.altText, r.title),
      body: r.body,
      isDemo: r.isDemo,
    };
  } else if (!result || result.kind === "error") {
    // Read error → try bundled fallback for graceful degradation.
    const fb = ARTICLES_FALLBACK.find((a) => a.slug === slug);
    if (fb) post = fb;
  }

  if (!post) {
    throw notFound();
  }

  const listData = listQuery.data;
  let relatedPool: ArticleItem[] = [];
  if (listData && listData.length > 0) {
    relatedPool = listData.map((r) => {
      const image = isValidHttpsUrl(r.imageUrl)
        ? r.imageUrl
        : imageForSlug(r.slug) ?? JOURNAL_PLACEHOLDER;
      return {
        id: r.id,
        slug: r.slug,
        category: r.category,
        title: r.title,
        excerpt: r.excerpt,
        date: formatArticleDate(r.publishedAt),
        readTime: r.readTime,
        author: r.author,
        image,
        alt: deriveAlt(r.altText, r.title),
        body: r.body,
        isDemo: r.isDemo,
      };
    });
  } else if (listData === null || listData === undefined) {
    relatedPool = ARTICLES_FALLBACK;
  }
  const related = relatedPool.filter((p) => p.slug !== post!.slug).slice(0, 3);

  return (
    <SiteLayout>
      {/* Header */}
      <Container className="pt-16 md:pt-24">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden strokeWidth={1.75} />
          Journal
        </Link>

        <div className="mt-8 flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary">
          <span>{post.category}</span>
          {post.isDemo ? (
            <span className="rounded-full bg-secondary px-2 py-0.5 text-foreground/70">
              Demo Content
            </span>
          ) : null}
        </div>

        <h1 className="mt-4 max-w-3xl font-display text-4xl leading-tight md:text-6xl">
          {post.title}
        </h1>

        <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
          {post.excerpt}
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {post.author ? <span>{post.author}</span> : null}
          {post.author && post.date ? <span aria-hidden>·</span> : null}
          {post.date ? <span>{post.date}</span> : null}
          {post.date && post.readTime ? <span aria-hidden>·</span> : null}
          {post.readTime ? <span>{post.readTime}</span> : null}
        </div>
      </Container>

      {/* Cover image */}
      <Section className="pt-10">
        <div className="relative overflow-hidden rounded-3xl border border-border">
          <img
            src={post.image}
            alt={post.alt}
            loading="lazy"
            className="aspect-[16/9] w-full object-cover"
          />
          {post.isDemo ? (
            <span className="absolute left-4 top-4 rounded-full bg-background/85 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.15em] text-foreground backdrop-blur">
              Demo Content
            </span>
          ) : null}
        </div>
      </Section>

      {/* Body */}
      <Section>
        <div className="mx-auto max-w-2xl">
          {post.isDemo ? (
            <div className="rounded-2xl border border-dashed border-primary/40 bg-primary/5 px-5 py-4 text-sm text-foreground/80">
              <span className="font-medium text-foreground">Demo Content —</span>{" "}
              This article is an illustrative example. It'll be replaced as the
              Poonam Kapur team publishes real writing.
            </div>
          ) : null}
          <div className="mt-10 space-y-6 text-base leading-relaxed text-foreground/85 md:text-lg">
            {post.body.map((para: string, i: number) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          <div className="mt-12 rounded-3xl border border-border bg-card p-6 md:p-8">
            <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Enjoyed this?
            </span>
            <h2 className="mt-3 font-display text-2xl font-medium md:text-3xl">
              Talk to us on WhatsApp.
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Questions about the menu, sourcing or a custom plan — send us a
              note and we'll reply the same day.
            </p>
            <WhatsAppActions
              className="mt-6"
              location="journal-article"
              compact
              chatLabel="Chat on WhatsApp"
            />
          </div>
        </div>
      </Section>

      {/* Related posts */}
      {related.length > 0 && (
        <Section className="bg-secondary/30">
          <div className="max-w-2xl">
            <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Keep reading
            </span>
            <h2 className="mt-3 font-display text-3xl font-medium md:text-4xl">
              More from the Journal.
            </h2>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {related.map((p) => (
              <Link
                key={p.id}
                to="/blog/$slug"
                params={{ slug: p.slug }}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-shadow duration-300 hover:shadow-sm"
              >
                <div className="relative">
                  <img
                    src={p.image}
                    alt={p.alt}
                    loading="lazy"
                    className="aspect-[16/10] w-full object-cover"
                  />
                  {p.isDemo ? (
                    <span className="absolute left-3 top-3 rounded-full bg-background/85 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.15em] text-foreground backdrop-blur">
                      Demo Content
                    </span>
                  ) : null}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    {p.category}
                  </div>
                  <h3 className="mt-3 font-display text-lg font-medium leading-snug group-hover:text-primary">
                    {p.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}
    </SiteLayout>
  );
}